import {
	defaultAttachmentTypeLabelMap,
	IndemnityStatus,
	SuretyAccountId,
	SuretyIndemnityAgreementId,
} from "@inrev/common";
import { accountIndemnityAgreementsQueryKey } from "@portal/api/account";
import {
	useAdminDeleteIndemnityAgreementFile,
	useAdminGetAccountIndemnityAgreementFile,
	useAdminGetAccountIndemnityAgreementFileUrl,
} from "@portal/api/account/admin";
import { useMemo } from "react";
import { AttachmentFileCardLayout } from "../../layout/AttachmentLayout";
import { type PDFViewerControl } from "../../patterns/pdf-viewer";
import { AttachmentActions } from "../../ui/Attachment/AttachmentActions";
import { Tag } from "../../ui/Tag";

export const AdminIndemnityAgreementAttachmentCard = ({
	suretyAccountId,
	indemnityAgreementId,
	status,
}: {
	suretyAccountId: SuretyAccountId;
	indemnityAgreementId: SuretyIndemnityAgreementId;
	status: IndemnityStatus;
}) => {
	const { indemnityAgreementFile, indemnityAgreementFileLoading } =
		useAdminGetAccountIndemnityAgreementFile(suretyAccountId, indemnityAgreementId);

	const { downloadIndemnityAgreement, indemnityAgreementFileUrlLoading } =
		useAdminGetAccountIndemnityAgreementFileUrl(suretyAccountId, indemnityAgreementId, true);

	const { deleteIndemnityAgreementFile } = useAdminDeleteIndemnityAgreementFile(
		suretyAccountId,
		indemnityAgreementId,
	);

	const indemnityFileId = indemnityAgreementFile?.id;
	const viewControl = useMemo<PDFViewerControl>(
		() => ({
			fetchUrl: `/v2/admin/surety/accounts/${suretyAccountId}/indemnity-agreements/${indemnityAgreementId}`,
			queryKey: accountIndemnityAgreementsQueryKey(suretyAccountId, indemnityAgreementId),
			fileId: indemnityFileId,
		}),
		[suretyAccountId, indemnityAgreementId, indemnityFileId],
	);

	// if we just don't have a file for this agreement
	if (!indemnityAgreementFile) {
		return null;
	}

	const headerSection = indemnityAgreementFile.name;

	// Uncomment when we add the uploadedBy and created at fields to the uploaded file schema
	// const subHeaderSection = (
	// 	<div className="text-tertiary text-[12px] flex gap-x-[40px]">
	// 		<div>
	// 			{indemnityAgreementFile.createdAt &&
	// 				DateTime.fromISO(indemnityAgreementFile.createdAt).toFormat("LLL dd, yyyy")}
	// 		</div>
	// 		{indemnityAgreementFile.uploadedBy && (
	// 			<div>
	// 				{indemnityAgreementFile.uploadedBy.firstName} {indemnityAgreementFile.uploadedBy.lastName}
	// 			</div>
	// 		)}
	// 	</div>
	// );

	const tagSection = (
		<>
			{indemnityAgreementFile.types.map((fileType) => (
				<Tag key={fileType}>{defaultAttachmentTypeLabelMap[fileType]}</Tag>
			))}
		</>
	);

	const actionsSection = (
		<AttachmentActions
			analyticsContext="indemnity"
			fileExtension={indemnityAgreementFile.extension}
			fileName={indemnityAgreementFile.name}
			enableDelete={status !== "complete"}
			enableDownload={true}
			enableView={true}
			viewControl={viewControl}
			onConfirmDelete={status !== "complete" ? deleteIndemnityAgreementFile : undefined}
			onConfirmDownload={downloadIndemnityAgreement}
			isLoading={indemnityAgreementFileUrlLoading}
		/>
	);

	return (
		<AttachmentFileCardLayout
			fileExtension={indemnityAgreementFile.extension}
			headerSection={headerSection}
			tagSection={tagSection}
			actionSection={actionsSection}
			isLoading={indemnityAgreementFileLoading}
		/>
	);
};
